import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Input,
  Checkbox,
  Button,
  Space,
  Card,
  Spin,
  Typography,
  Radio,
} from "antd";
import axios from "axios";
import AuthContext from "../context/authContext";
import { reflectionForms } from "../config/reflectionForms";
import SpeechInput from "../components/SpeechInput";

import { SwapRightOutlined } from "@ant-design/icons";

import reflection_selected from "../img/strategies_icons/reflection.png";

const { Text } = Typography;

const ReflectionForm = () => {
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document);

  const [loading, setLoading] = useState(true);
  const { taskId, reflectionType } = useParams();
  const { currentUser } = useContext(AuthContext);

  const formConfig = reflectionForms[reflectionType];
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const fetchAnswers = async () => {
      try {
        const res = await axios.get(`/api/reflection/${taskId}`);
        if (res.data) setAnswers(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [taskId]);

  const handleChange = (name, value) => {
    setAnswers({ ...answers, [name]: value });
  };

  const handleSave = async () => {
    if (!taskId) return alert("taskId fehlt!");
    try {
      await axios.post("/api/reflection/save", {
        task_id: taskId,
        member_id: currentUser.id,
        reflection_type: reflectionType,
        answers,
      });

      alert("Super! Deine Antworten wurden gespeichert 🎉");
    } catch (err) {
      console.error(err);
      alert("Fehler beim Speichern");
    }
  };

  if (!formConfig) return <div>Form not found</div>;

  return (
    <div style={{ padding: "30px", overflow: "auto" }}>
      <Card className="panel-card">
        <Spin spinning={loading}>
          {/* HEADER */}
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 12,
              }}
            >
              <img
                src={reflection_selected}
                alt="Reflection"
                style={{ width: 60, height: 60 }}
              />
                <h2 style={{ margin: 0 }}>{formConfig.description}</h2>
            </div>

          
          </div>

          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {formConfig.fields.map((field) => {
              /* INFO BLOCK */
              if (field.type === "info") {
                return (
                  <div
                    key={field.label}
                    style={{ textAlign: "center", fontSize: 18 }}
                  >
                    <Text strong className="reflection-label">{field.label}</Text>
                  </div>
                );
              }

              /* TEXTAREA */
              if (field.type === "textarea") {
                return (
                  <Card
                    key={field.name}
                    className="reflection-card reflection-card--textarea"
                  >
                    <Text strong className="reflection-label">
                      {field.label}
                    </Text>

                    <div style={{ marginTop: 10 }}>
                      {isIOS ? (
                        <Input.TextArea
                          value={answers[field.name] || ""}
                          onChange={(e) =>
                            handleChange(field.name, e.target.value)
                          }
                          placeholder="Antwort eingeben..."
                          autoSize={{ minRows: 3, maxRows: 8 }}
                        />
                      ) : (
                        <SpeechInput
                          multiline
                          value={answers[field.name] || ""}
                          onChange={(val) => handleChange(field.name, val)}
                          placeholder="Antwort eingeben oder sprechen 🎤"
                        />
                      )}
                    </div>
                  </Card>
                );
              }

              /* CHECKBOX */
              if (field.type === "checkbox") {
                return (
                  <Card
                    key={field.name}
                    className="reflection-card reflection-card--checkbox"
                  >
                    <Text strong className="reflection-label">
                      {field.label}
                    </Text>

                    <div style={{ marginTop: 12 }}>
                      <Checkbox.Group
                        value={answers[field.name] || []}
                        options={field.options}
                        onChange={(vals) => handleChange(field.name, vals)}
                      />
                    </div>
                  </Card>
                );
              }

              /* RADIO */
              if (field.type === "radio") {
                return (
                  <Card
                    key={field.name}
                    className="reflection-card reflection-card--radio"
                  >
                    <Text strong className="reflection-label">
                      {field.label}
                    </Text>

                    <div style={{ marginTop: 12 }}>
                      <Radio.Group
                        value={answers[field.name]}
                        onChange={(e) =>
                          handleChange(field.name, e.target.value)
                        }
                      >
                        <Space direction="vertical">
                          {field.options.map((opt) => (
                            <Radio key={opt} value={opt}>
                              {opt}
                            </Radio>
                          ))}
                        </Space>
                      </Radio.Group>
                    </div>
                  </Card>
                );
              }

              /* LIST (your vocab inputs) */
              if (field.type === "list") {
                const rows = field.rows || 3;
                const values =
                  answers[field.name] ||
                  Array(rows).fill({ word: "", meaning: "" });

                const updateRow = (index, key, value) => {
                  const updated = [...values];
                  updated[index] = {
                    ...updated[index],
                    [key]: value,
                  };
                  handleChange(field.name, updated);
                };

                return (
                  <Card
                    key={field.name}
                    className="reflection-card reflection-card--field"
                  >
                   <Text strong className="reflection-label">
                      {field.label}
                    </Text>

                    <Space
                      direction="vertical"
                      style={{ width: "100%", marginTop: 12 }}
                    >
                      {Array.from({ length: rows }).map((_, i) => (
                        <Space key={i} className="reflection-list-row">
                          <div className="word-col">
                            <span className="field-label">Wort</span>
                            <Input
                              placeholder="z.B. mutig"
                              value={values[i]?.word || ""}
                              onChange={(e) =>
                                updateRow(i, "word", e.target.value)
                              }
                            />
                          </div>

                          <div className="arrow">
                            <SwapRightOutlined />
                          </div>

                          <div className="meaning-col">
                            <span className="field-label">Bedeutung</span>
                            <Input
                              placeholder="z.B. brave"
                              value={values[i]?.meaning || ""}
                              onChange={(e) =>
                                updateRow(i, "meaning", e.target.value)
                              }
                            />
                          </div>
                        </Space>
                      ))}
                    </Space>
                  </Card>
                );
              }

              return null;
            })}

            <div className="reflection-save-wrapper">
              {/* SAVE BUTTON */}
              <Button type="primary" size="large" onClick={handleSave}>
                Antworten speichern
              </Button>
            </div>
          </Space>
        </Spin>
      </Card>
    </div>
  );
};

export default ReflectionForm;
